import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';

const AddressHoldersTab = ({ address, addressData }) => {
  const [holders, setHolders] = useState([]);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [totalHolders, setTotalHolders] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' for highest to lowest, 'asc' for lowest to highest


  useEffect(() => {
    const fetchHolders = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if this is a token contract
        if (addressData?.type === 'contract' && addressData?.contractType === 'ERC20') {
          // Fetch token holders
          const holdersData = await apiService.getTokenHolders(address, itemsPerPage, (currentPage - 1) * itemsPerPage);
          
          console.log('Holders data received:', holdersData);
          const holdersArray = holdersData.holders || [];
          
          // Sort holders by balance
          const sortedHolders = [...holdersArray].sort((a, b) => {
            const balanceA = parseFloat(a.balanceFormatted) || 0;
            const balanceB = parseFloat(b.balanceFormatted) || 0;
            
            if (sortOrder === 'desc') {
              return balanceB - balanceA; // Highest to lowest
            } else {
              return balanceA - balanceB; // Lowest to highest
            }
          });
          
          setHolders(sortedHolders);
          setTotalHolders(holdersData.totalHolders || 0);
          setTokenInfo(holdersData.tokenInfo);
        } else {
          // For non-token addresses, show message
          setError('Holders information is only available for token contracts.');
        }
      } catch (error) {
        console.error('Error fetching holders:', error);
        setError('Failed to load holders data.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHolders();
  }, [address, addressData, currentPage, itemsPerPage, sortOrder]);

  const formatBalance = (balance, decimals = 18) => {
    try {
      const value = parseFloat(balance);
      if (value === 0) return '0';
      if (value < 0.0001) return '< 0.0001';
      return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
    } catch (error) {
      return balance;
    }
  };

  const formatPercentage = (percentage) => {
    try {
      const value = parseFloat(percentage);
      if (value < 0.01) return '< 0.01%';
      return `${value.toFixed(2)}%`;
    } catch (error) {
      return percentage;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <div className="text-gray-400 mb-4">{error}</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="holders-tab"
    >
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold mb-2">Token Holders</h3>
            {tokenInfo && (
              <p className="text-gray-400">
                {totalHolders.toLocaleString()} holders of {tokenInfo.name} ({tokenInfo.symbol})
              </p>
            )}
          </div>
          
          {/* Sort Controls */}
          <div className="mt-4 md:mt-0">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="bg-dark-200 text-white px-3 py-2 rounded border border-gray-600 focus:border-primary focus:outline-none"
            >
              <option value="desc">Highest to Lowest</option>
              <option value="asc">Lowest to Highest</option>
            </select>
          </div>
        </div>
      </div>


      {/* Holders List */}
      {holders.length > 0 ? (
        <div className="space-y-4">
          {/* Table Header */}
          <div className="hidden md:grid grid-cols-12 gap-4 p-4 bg-dark-200 rounded-lg text-sm font-medium text-gray-400">
            <div className="col-span-1">Rank</div>
            <div className="col-span-5">Address</div>
            <div className="col-span-3">
              Balance 
              {sortOrder === 'desc' ? ' ↓' : ' ↑'}
            </div>
            <div className="col-span-3">Percentage</div>
          </div>

          {/* Holders */}
          {holders.map((holder, index) => (
            <motion.div
              key={holder.address}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-dark-100 rounded-lg hover:bg-opacity-80 transition-colors"
            >
              {/* Rank */}
              <div className="md:col-span-1">
                <span className="text-sm text-gray-400 md:hidden">Rank: </span>
                <span className="font-mono">#{(currentPage - 1) * itemsPerPage + index + 1}</span>
              </div>

              {/* Address */}
              <div className="md:col-span-5">
                <span className="text-sm text-gray-400 md:hidden">Address: </span>
                <Link 
                  to={`/address/${holder.address}`}
                  className="font-mono text-primary hover:underline break-all"
                >
                  {holder.address}
                </Link>
              </div>

              {/* Balance */}
              <div className="md:col-span-3">
                <span className="text-sm text-gray-400 md:hidden">Balance: </span>
                <div>
                  <div className="font-mono">
                    {formatBalance(holder.balanceFormatted)}
                  </div>
                  {tokenInfo && (
                    <div className="text-sm text-gray-400">
                      {tokenInfo.symbol}
                    </div>
                  )}
                </div>
              </div>

              {/* Percentage */}
              <div className="md:col-span-3">
                <span className="text-sm text-gray-400 md:hidden">Percentage: </span>
                <div className="font-mono text-accent-teal">
                  {formatPercentage(holder.percentage)}
                </div>
              </div>
            </motion.div>
          ))}

          {/* Pagination */}
          {totalHolders > itemsPerPage && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                {currentPage > 1 && (
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="px-3 py-2 bg-dark-200 text-white rounded hover:bg-opacity-80"
                  >
                    Previous
                  </button>
                )}
                
                <span className="px-3 py-2 text-gray-400">
                  Page {currentPage} of {Math.ceil(totalHolders / itemsPerPage)}
                </span>
                
                {currentPage < Math.ceil(totalHolders / itemsPerPage) && (
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    className="px-3 py-2 bg-dark-200 text-white rounded hover:bg-opacity-80"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="text-gray-400">No holders found for this token.</div>
        </div>
      )}
    </motion.div>
  );
};

export default AddressHoldersTab;
